import { useEffect, useState } from 'react';
import type { Scooter } from '../services/api/types';
import { packService } from '../services/api';
import { chipFromAccessState, type ScooterChipState } from '../utils/rideReadiness';

export { chipFromAccessState };
export type { ScooterChipState };

// Chip states surfaced by the rider portal:
//  - `ready`       - backend access_state is active and readiness.ready is true.
//  - `needs_packs` - backend access_state is grace/system_blocked, or active
//                    with readiness.ready false.
//  - `restricted`  - backend access_state is admin_blocked.
//  - `unavailable` - the readiness lookup failed (network/5xx). Render as
//                    a muted, non-claiming chip so we don't show "Ready" or
//                    "Needs Pack(s)" based on stale/missing data.
//  - `checking`    - transient state while the lookup is in flight.
interface CacheEntry {
  state: ScooterChipState;
  fetchedAt: number;
}

// Module-level cache so AccountPage and ScooterSelector don't double-fetch.
// A 30s TTL is short enough that toggling auto-activation or buying a pack
// reflects on the next visit, long enough to avoid re-fetching on every
// route hop. Call `invalidateScooterReadiness(vin)` from pack-purchase
// success paths to force a re-fetch before the TTL elapses.
const cache = new Map<string, CacheEntry>();
const TTL_MS = 30_000;

// In-flight de-dup so two components mounting simultaneously share a single
// network request per VIN.
const inFlight = new Map<string, Promise<ScooterChipState>>();
const cacheGeneration = new Map<string, number>();

// Cache version + subscribers. Mounted hooks subscribe so that an external
// invalidation re-runs their fetch effect; without this, only a fresh
// mount would see the updated readiness.
let cacheVersion = 0;
const versionSubscribers = new Set<() => void>();

export function invalidateScooterReadiness(vin?: string): void {
  if (vin) {
    cache.delete(vin);
    cacheGeneration.set(vin, (cacheGeneration.get(vin) || 0) + 1);
    inFlight.delete(vin);
  } else {
    cache.clear();
    inFlight.clear();
    cacheGeneration.clear();
  }
  cacheVersion += 1;
  versionSubscribers.forEach(fn => fn());
}

async function fetchReadiness(vin: string): Promise<ScooterChipState> {
  const existing = inFlight.get(vin);
  if (existing) return existing;

  const generation = cacheGeneration.get(vin) || 0;
  let promise!: Promise<ScooterChipState>;
  promise = (async (): Promise<ScooterChipState> => {
    try {
      const summary = await packService.getDashboardPackSummary(vin);
      const chip: ScooterChipState =
        chipFromAccessState(summary.readiness.access_state, summary.readiness.ready)
        ?? (summary.readiness.ready ? 'ready' : 'needs_packs');
      if ((cacheGeneration.get(vin) || 0) === generation) {
        cache.set(vin, { state: chip, fetchedAt: Date.now() });
      }
      return chip;
    } catch (err) {
      console.error('useScooterReadiness: lookup failed for', vin, err);
      // Don't cache failures; let the next mount or invalidation retry.
      throw err;
    } finally {
      if (inFlight.get(vin) === promise) inFlight.delete(vin);
    }
  })();

  inFlight.set(vin, promise);
  return promise;
}

export function chipLabel(state: ScooterChipState): string {
  switch (state) {
    case 'restricted':
      return 'Restricted';
    case 'needs_packs':
      return 'Needs Pack(s)';
    case 'unavailable':
      return 'Unavailable';
    case 'ready':
      return 'Ready';
    case 'checking':
    default:
      return 'Checking';
  }
}

export function useScooterReadiness(
  scooters: Scooter[],
): Record<string, ScooterChipState> {
  const buildSeed = (): Record<string, ScooterChipState> => {
    const seed: Record<string, ScooterChipState> = {};
    const now = Date.now();
    scooters.forEach(s => {
      const accessChip = chipFromAccessState(s.access_state);
      if (accessChip) {
        seed[s.vin] = accessChip;
        return;
      }
      const cached = cache.get(s.vin);
      seed[s.vin] =
        cached && now - cached.fetchedAt < TTL_MS ? cached.state : 'checking';
    });
    return seed;
  };

  const [readiness, setReadiness] = useState<Record<string, ScooterChipState>>(
    buildSeed,
  );

  // Subscribe to cache invalidations so mounted hooks re-fetch when a pack
  // purchase (or other side-effect) clears the cache externally.
  const [localCacheVersion, setLocalCacheVersion] = useState(cacheVersion);
  useEffect(() => {
    const onChange = () => setLocalCacheVersion(cacheVersion);
    versionSubscribers.add(onChange);
    return () => {
      versionSubscribers.delete(onChange);
    };
  }, []);

  useEffect(() => {
    const currentVins = new Set(scooters.map(scooter => scooter.vin));
    // A refetch is authoritative for the signed-in rider. Remove readiness
    // and in-flight responses for VINs that ownership no longer includes.
    Array.from(cache.keys()).forEach(vin => {
      if (!currentVins.has(vin)) {
        cache.delete(vin);
        cacheGeneration.set(vin, (cacheGeneration.get(vin) || 0) + 1);
        inFlight.delete(vin);
      }
    });
    if (scooters.length === 0) {
      setReadiness({});
      return;
    }

    let cancelled = false;
    setReadiness(buildSeed());

    const toFetch = scooters.filter(s => {
      const cached = cache.get(s.vin);
      return !cached || Date.now() - cached.fetchedAt >= TTL_MS;
    });

    if (toFetch.length === 0) return;

    Promise.all(
      toFetch.map(s =>
        fetchReadiness(s.vin).then(
          state => ({ vin: s.vin, state }),
          () => ({
            vin: s.vin,
            state: chipFromAccessState(s.access_state) ?? 'unavailable',
          }),
        ),
      ),
    ).then(results => {
      if (cancelled) return;
      const updates: Record<string, ScooterChipState> = {};
      results.forEach(entry => {
        if (entry) updates[entry.vin] = entry.state;
      });
      if (Object.keys(updates).length === 0) return;
      setReadiness(prev => ({ ...prev, ...updates }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scooters, localCacheVersion]);

  return readiness;
}
